import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    Input,
    Output,
    signal,
    untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { CtrlDateInputDirective, CtrlErrorMessageDirective } from '@co-3/ctrl-ui/forms';
import { CtrlIconComponent } from '@co-3/ctrl-ui/icons';
import { CtrlModal, CtrlModalComponent } from '@co-3/ctrl-ui/modals';
import { isCurrentlyActive, PaymentMethod } from '@models/payment-method';
import { PaymentMethodsService } from '../../payment-methods.service';
import { RequiredInfoComponent } from '../../../shared/components/required-info/required-info.component';
import { SharedModule } from '../../../shared/shared.module';
import { CommonValidators } from '../../../shared/validators/common-validators';
import { dateAfter, dateNotIn } from '../../../shared/validators/date.validator';
import { format, isSameDay } from 'date-fns';
import { parseDateString } from '@co-3/ctrl-ui/common';
import { addDays } from 'date-fns/addDays';
import { finalize } from 'rxjs';
import {
    IBAN_REQUIRED,
    IBAN_BANK_CODE,
    IBAN_CHECKSUM,
    IBAN_PATTERN,
    NAME_OF_BANK_REQUIRED,
    NAME_OF_BANK_TOO_SHORT,
    ACCOUNT_HOLDER_REQUIRED,
    ACCOUNT_HOLDER_TOO_SHORT,
    BIC_REQUIRED,
    IBAN_NO_COUNTRY_CODE,
} from '@config/error-messages.const';
import { validateIban } from 'app/shared/validators/iban.validator';
import { PortalActionButtonsComponent } from '../../components';

export type EditPaymentMethodMode = 'editCurrent' | 'addQueued' | 'editQueued';

@Component({
    selector: 'app-edit-payment-method-modal',
    templateUrl: './edit-payment-method-modal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CtrlModalComponent,
        CtrlIconComponent,
        ReactiveFormsModule,
        SharedModule,
        CtrlDateInputDirective,
        RequiredInfoComponent,
        CtrlErrorMessageDirective,
        PortalActionButtonsComponent,
    ],
})
export class EditPaymentMethodModalComponent implements CtrlModal<PaymentMethod[]> {
    private paymentMethodsService = inject(PaymentMethodsService);
    private allPaymentMethods = toSignal(this.paymentMethodsService.paymentMethodsForSelectedContract$);
    private validFromDates = computed(() => this.allPaymentMethods().map((paymentMethod) => paymentMethod.validFrom));
    private formBuilder = inject(FormBuilder);

    @Output() public dismiss: EventEmitter<PaymentMethod[]> = new EventEmitter<PaymentMethod[]>();

    @Input({ required: true })
    public mode = 'editCurrent' as EditPaymentMethodMode;

    @Input({ required: true })
    public paymentMethod: PaymentMethod;

    // #region errormessages
    protected ibanErrorMessages = {
        required: IBAN_REQUIRED,
        bankCode: IBAN_BANK_CODE,
        checksum: IBAN_CHECKSUM,
        type: IBAN_REQUIRED,
        bBanPattern: IBAN_PATTERN,
        noCountryPatternFound: IBAN_NO_COUNTRY_CODE,
    };
    protected bankNameErrorMessages = {
        required: NAME_OF_BANK_REQUIRED,
        minlength: NAME_OF_BANK_TOO_SHORT,
    };
    protected accountHolderErrorMessages = {
        required: ACCOUNT_HOLDER_REQUIRED,
        minlength: ACCOUNT_HOLDER_TOO_SHORT,
    };
    protected bicErrorMessages = {
        required: BIC_REQUIRED,
    };
    // #endregion
    protected formGroup = this.formBuilder.group(
        {
            accountHolder: ['', CommonValidators.something],
            bankName: ['', CommonValidators.something],
            iban: ['', [Validators.required, validateIban]],
            bic: [''],
            validFrom: [''],
        },
        { validators: bicValidator },
    );
    private ibanValue = toSignal(this.formGroup.controls.iban.valueChanges);

    protected updating = signal(false);

    public constructor() {
        effect(this.toggleDisabledBicControlForGermanIban);
    }

    public ngOnChanges(): void {
        if (this.paymentMethod) {
            const { validFrom, ...rest } = this.paymentMethod;
            this.formGroup.reset({
                validFrom: format(validFrom, 'dd.MM.yyyy'),
                ...rest,
            });
        }
        if (this.mode === 'addQueued' || this.mode === 'editQueued') {
            this.formGroup.controls.validFrom.setValidators([
                Validators.required,
                dateAfter(addDays(new Date(), 1)),
                dateNotIn(this.validFromDates().filter((date) => !isSameDay(date, this.paymentMethod.validFrom))),
            ]);
        }
        if (this.mode === 'addQueued') {
            this.formGroup.controls.validFrom.reset(format(addDays(new Date(), 1), 'dd.MM.yyyy'));
        }
    }

    protected isCurrentIbanGerman(): boolean {
        const ibanControl = this.formGroup.controls.iban;
        return (
            ibanControl.valid &&
            typeof ibanControl.value === 'string' &&
            ibanControl.value.toLowerCase().startsWith('de')
        );
    }

    protected handleSubmit(): void {
        const { validFrom, ...newValues } = this.formGroup.value;
        const { id, validFrom: oldValidFrom, ...oldValues } = this.paymentMethod;
        let createNewPaymentMethod: boolean;

        let newValidFrom = parseDateString(validFrom, 'dd.MM.yyyy');

        if (this.mode === 'editCurrent' && !isCurrentlyActive(this.paymentMethod)) {
            throw new Error('Trying to edit a queued PaymentMethod as if it was the currently active one');
        }

        switch (this.mode) {
            case 'editCurrent':
                // When editing the current payment method, the resulting method will be valid from the same day
                newValidFrom = new Date();
                // A newly created payment method may be edited on the same day it was created
                if (isSameDay(oldValidFrom, new Date())) {
                    createNewPaymentMethod = false;
                } else {
                    // If a payment method is already active (validFrom is in the past), then we cannot edit it
                    // We then create a new Payment method that will be queued (it is valid from today on)
                    createNewPaymentMethod = true;
                }
                break;
            case 'addQueued':
                createNewPaymentMethod = true;
                break;
            case 'editQueued':
                createNewPaymentMethod = false;
        }

        const newPaymentMethod = {
            ...oldValues,
            ...newValues,
            validFrom: newValidFrom,
            validTo: null,
            bankCountry: newValues.iban.substring(0, 2),
        };

        this.updating.set(true);

        if (createNewPaymentMethod) {
            this.paymentMethodsService
                .create(newPaymentMethod)
                .pipe(finalize(() => this.updating.set(false)))
                .subscribe(() => this.dismiss.emit());
        } else {
            this.paymentMethodsService
                .update({ ...newPaymentMethod, id })
                .pipe(finalize(() => this.updating.set(false)))
                .subscribe(() => this.dismiss.emit());
        }
    }

    private toggleDisabledBicControlForGermanIban = (): void => {
        this.ibanValue();

        untracked(() => {
            if (this.isCurrentIbanGerman()) {
                this.formGroup.controls.bic.disable();
            } else {
                this.formGroup.controls.bic.enable();
            }
        });
    };
}

const bicValidator: ValidatorFn = (form: FormGroup): ValidationErrors | null => {
    const bicControl = form.get('bic');
    const ibanControl = form.get('iban');

    if (
        ibanControl.valid &&
        typeof ibanControl.value === 'string' &&
        !ibanControl.value.toLowerCase().startsWith('de')
    ) {
        for (let i = 0; i < CommonValidators.bic.length; i++) {
            const error = CommonValidators.bic[i](bicControl);
            if (error !== null) {
                bicControl.setErrors(error);
                return error;
            }
        }
    }

    bicControl.setErrors(null);
    return null;
};
